import { useEffect, useState } from 'react';
import useFetchErrorByKey from './useFetchErrorsByKey.tsx';
import useMarketInfo from '../../hooks/useMarketInfo.tsx';
import { useRegistrationStore } from '../../store/store.ts';
import { TDepositLimits } from '../../types/types.ts';
import { HEADER_NAMES } from '../../utils/constants.ts';
import { currencyByCountryCode } from '../../utils/countries.ts';
import { API_URL_SECONDARY } from '../../utils/envConstants.ts';
import { useFetcher } from '../swr.ts';

type TLimitType = 'minAllowedLimits' | 'maxAllowedLimits';
type TTimePeriod = 'Monthly' | 'Weekly' | 'Daily';
type TResponse = {
  limits?: {
    money?: {
      deposit?: {
        [key in TLimitType]?: {
          [key in TTimePeriod]?: {
            iso4217CurrencyCode: string;
            amount: number;
          }[];
        };
      };
    };
  };
}

const useValidationLimits = () => {
  const fetcher = useFetcher();
  const { setErrors, setCustomValidations, formData } = useRegistrationStore();
  const { countryCode, jurisdiction } = useMarketInfo();
  const currency = currencyByCountryCode[countryCode] || 'EUR';
  const [depositLimits, setDepositLimits] = useState<TDepositLimits | null | undefined>();
  const getErrorByKey = useFetchErrorByKey;
  const limitToLowError = getErrorByKey('limit_too_low');
  const limitToHighError = getErrorByKey('limit_too_high');

  const getFilteredLimit = (
    response: TResponse,
    limitType: TLimitType,
    timePeriod: TTimePeriod,
    currency: string,
  ): number | null => {
    const limits = response?.limits?.money?.deposit?.[limitType]?.[timePeriod];

    return limits?.find((item) => item.iso4217CurrencyCode === currency)?.amount ?? null;
  };

  useEffect(() => {
    const getDepositLimits = async (
      currency: string,
      jurisdiction?: string,
    ): Promise<TDepositLimits | null> => {
      const response = await fetcher(`${API_URL_SECONDARY}/casino-player/playok/api/config/${jurisdiction}`, {
        method:  'GET',
        headers: {
          [HEADER_NAMES.CASUMO_BRAND_ID]: '1',
          'Accept':                       'application/json',
          'Content-Type':                 'application/json',
        },
      });

      return ['Monthly', 'Weekly', 'Daily'].reduce((acc, period) => {
        acc.minAllowedLimits[period.toLowerCase() as 'daily' | 'weekly' | 'monthly'] = getFilteredLimit(
          response, 'minAllowedLimits', period as TTimePeriod, currency,
        );
        acc.maxAllowedLimits[period.toLowerCase() as 'daily' | 'weekly' | 'monthly'] = getFilteredLimit(
          response, 'maxAllowedLimits', period as TTimePeriod, currency,
        );

        return acc;
      }, {
        minAllowedLimits: { daily: null, weekly: null, monthly: null },
        maxAllowedLimits: { daily: null, weekly: null, monthly: null },
      } as TDepositLimits);
    };

    getDepositLimits(currency, jurisdiction).then((data) => {
      setDepositLimits(data);
    });
  }, [currency, jurisdiction]);

  const validateLimits = async (limit: 'daily' | 'weekly' | 'monthly') => {
    const validationErrors: Record<string, string> = {};
    const limitFields = [limit];

    if (!depositLimits) return;

    limitFields.forEach(field => {
      const limit = parseFloat(formData?.[field] || '');
      const minLimit = depositLimits?.minAllowedLimits[field] || 0;
      const maxLimit = depositLimits?.maxAllowedLimits[field] || Infinity;

      if (limit < minLimit) validationErrors[field] = limitToLowError + ' ' + minLimit;
      if (limit > maxLimit) validationErrors[field] = limitToHighError + ' ' + maxLimit;
    });

    setCustomValidations(validationErrors);
    setErrors(validationErrors);
  };

  return { validateLimits };
};

export default useValidationLimits;
